
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface Review {
  id: string;
  title: string;
  content: string;
  author: string;
  category: string;
  rating: number;
  cover_image: string;
  genre: string;
  verdict: string;
  bestseller: boolean;
  our_pick: boolean;
  created_at: string;
}

const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    return (
      <div className="flex items-center gap-1">
        {[...Array(fullStars)].map((_, i) => (
          <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>
        ))}
        {hasHalfStar && <i className="ri-star-half-fill text-yellow-400 text-sm"></i>}
        {[...Array(5 - Math.ceil(rating))].map((_, i) => (
          <i key={i} className="ri-star-line text-gray-300 text-sm"></i>
        ))}
        <span className="ml-1 text-xs text-gray-600">{rating}/5</span>
      </div>
    );
  };

export default function FeaturedReviews() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedReviews();
  }, []);

  const fetchFeaturedReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('published', true)
        .eq('featured', true)
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) {
        console.error('Error fetching reviews:', error);
      } else {
        setReviews(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Libre Baskerville", serif' }}>
              Featured Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our most compelling book reviews, carefully selected for their insight and literary merit
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden animate-pulse">
                <div className="h-64 bg-gray-300"></div>
                <div className="p-6">
                  <div className="h-4 bg-gray-300 rounded mb-2"></div>
                  <div className="h-6 bg-gray-300 rounded mb-3"></div>
                  <div className="h-4 bg-gray-300 rounded mb-4"></div>
                  <div className="h-20 bg-gray-300 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (reviews.length === 0) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Libre Baskerville", serif' }}>
              Featured Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              No featured reviews available at the moment. Check back soon for our latest literary insights!
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Libre Baskerville", serif' }}>
            Featured Reviews
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover our most compelling book reviews, carefully selected for their insight and literary merit
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reviews.map((review) => (
            <div key={review.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="aspect-[3/4] relative">
                <img 
                  src={review.cover_image} 
                  alt={review.title}
                  className="w-full h-full object-cover object-top"
                />
                {review.bestseller && (
                  <div className="absolute top-3 left-3 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-semibold">
                    Bestseller
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{review.title}</h3>
                <p className="text-gray-600 mb-1">by {review.author}</p>
                <div className="flex items-center gap-2 mb-3">
                  {review.our_pick && (
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                      <span>🍁</span>
                      Our Pick
                    </span>
                  )}
                  {review.bestseller && (
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                      <i className="ri-award-line"></i>
                      Bestseller
                    </span>
                  )}
                  <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
                    {review.genre}
                  </span>
                </div>
                <p className="text-gray-700 text-sm mb-4 line-clamp-3">{review.content.substring(0, 150)}...</p>
                <Link 
                  to={`/reviews/${review.id}`}
                  className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors font-medium"
                >
                  Read Full Review
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <button 
            onClick={() => window.REACT_APP_NAVIGATE('/reviews')}
            className="bg-red-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors whitespace-nowrap"
          >
            View All Reviews
          </button>
        </div>
      </div>
    </section>
  );
}
